import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Paper, Snackbar, Stack, Table, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  AccountCode, BudgetLine, BudgetVersion, Paginated, PartDecision,
} from "../api/types";
import { BackButton } from "../components/BackButton";
import { DecisionDialog } from "../components/DecisionDialog";
import { PartialApproval } from "../components/PartialApproval";
import { DecisionHistory } from "../components/DecisionHistory";
import type { Column, Focus } from "../components/Sheet";
import { Sheet, parseNumber, parseText } from "../components/Sheet";
import { formatMoney, sumMoney } from "../lib/money";
import { STATUS_BUDZETU } from "./ProjectPage";

type Wiersz = BudgetLine & Record<string, unknown>;

const PUSTY: Wiersz = {
  id: null, account_code: "", description: "", quantity: "1.000", unit: "",
  rate: "0.0000", amount_net: "", vat_code: "23", note: "", sort_order: 0,
};

export function BudgetEditorPage() {
  const { code = "", id = "" } = useParams();
  const kolejka = useQueryClient();

  const { data: wersja } = useQuery({
    queryKey: ["budget", id],
    queryFn: () => api.get<BudgetVersion>(`/budgets/${id}`),
  });
  // Konta wyłącznie z planu projektu: ten sam kod w innym planie to inna
  // pozycja, więc lista zmieszana z kilku planów byłaby po prostu błędna.
  const planKont = wersja?.account_plan;
  const { data: plan } = useQuery({
    queryKey: ["account-codes", planKont],
    queryFn: () =>
      api.get<Paginated<AccountCode>>(
        `/catalog/account-codes?plan=${encodeURIComponent(planKont ?? "")}`,
      ),
    enabled: Boolean(planKont),
  });

  const [wiersze, setWiersze] = useState<Wiersz[]>([]);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [konflikt, setKonflikt] = useState<BudgetVersion | null>(null);
  const [komunikat, setKomunikat] = useState<string | null>(null);
  const [blad, setBlad] = useState<ApiError | null>(null);
  const [decyzja, setDecyzja] = useState<"approve" | "reject" | null>(null);
  const [czesciowa, setCzesciowa] = useState(false);

  // Wczytujemy pozycje do stanu tylko wtedy, gdy zmienia się WERSJA albo jej
  // numer zapisu. Reagowanie na każdą odpowiedź serwera kasowałoby
  // niezapisane zmiany przy pierwszym pobraniu w tle — użytkownik wpisuje,
  // wraca do okna i nie ma tego, co napisał.
  const wczytana = useRef("");
  useEffect(() => {
    if (!wersja) return;
    const znacznik = `${wersja.id}:${wersja.revision}`;
    if (wczytana.current === znacznik) return;
    wczytana.current = znacznik;
    setWiersze(wersja.lines.map((l) => ({ ...PUSTY, ...l })));
  }, [wersja]);

  const kody = useMemo(() => {
    const mapa = new Map<string, AccountCode>();
    (plan?.results ?? []).filter((c) => c.is_selectable).forEach((c) => mapa.set(c.code, c));
    return mapa;
  }, [plan]);

  const tylkoOdczyt = !wersja?.available_actions.includes("edit");

  const kolumny: Column<Wiersz>[] = useMemo(() => [
    {
      key: "account_code", label: "Konto", width: 90, parse: parseText,
      validate: (v) => (v && !kody.has(v) ? `Nie ma pozycji „${v}” w planie kont.` : undefined),
    },
    { key: "description", label: "Opis", width: 320, parse: parseText },
    { key: "quantity", label: "Ilość", width: 80, align: "right", parse: parseNumber },
    { key: "unit", label: "J.m.", width: 70, parse: parseText },
    { key: "rate", label: "Stawka", width: 100, align: "right", parse: parseNumber },
    {
      key: "amount_net", label: "Netto", width: 110, align: "right", parse: parseNumber,
      validate: (v) => (v === "" ? "Podaj kwotę albo ilość i stawkę." : undefined),
    },
    { key: "vat_code", label: "VAT", width: 60, parse: parseText },
    { key: "note", label: "Uwagi", width: 200, parse: parseText },
  ], [kody]);

  function zmien(r: number, key: string, value: string) {
    setWiersze((stare) => {
      const kopia = [...stare];
      const wiersz = { ...kopia[r], [key]: value } as Wiersz;
      // Kwota jest wartością rozstrzygającą, ale dopóki użytkownik jej nie
      // ruszył, podpowiadamy iloczyn — tak liczy się większość pozycji.
      if ((key === "quantity" || key === "rate") && !wiersz._amountTouched) {
        const iloczyn = Number(wiersz.quantity || 0) * Number(wiersz.rate || 0);
        wiersz.amount_net = Number.isFinite(iloczyn) ? iloczyn.toFixed(2) : "";
      }
      if (key === "amount_net") wiersz._amountTouched = true;
      kopia[r] = wiersz;
      return kopia;
    });
  }

  function wklej(start: Focus, blok: string[][]) {
    setWiersze((stare) => {
      const kopia = [...stare];
      blok.forEach((linia, dr) => {
        const r = start.row + dr;
        while (kopia.length <= r) kopia.push({ ...PUSTY });
        const wiersz = { ...kopia[r] } as Wiersz;
        linia.forEach((komorka, dc) => {
          const col = kolumny[start.col + dc];
          if (!col) return;
          (wiersz as Record<string, unknown>)[col.key] =
            col.parse ? col.parse(komorka) : komorka.trim();
          if (col.key === "amount_net") wiersz._amountTouched = true;
        });
        if (!wiersz._amountTouched && wiersz.quantity && wiersz.rate) {
          wiersz.amount_net = (Number(wiersz.quantity) * Number(wiersz.rate)).toFixed(2);
        }
        kopia[r] = wiersz;
      });
      return kopia;
    });
  }

  const zapisz = useMutation({
    mutationFn: () =>
      api.put<BudgetVersion>(`/budgets/${id}/lines`, {
        revision: wersja?.revision ?? 0,
        lines: wiersze
          .filter((w) => w.account_code && w.amount_net !== "")
          .map((w, i) => ({
            id: w.id ?? null, account_code: w.account_code, description: w.description,
            quantity: w.quantity || "0", unit: w.unit, rate: w.rate || "0",
            amount_net: w.amount_net, vat_code: w.vat_code || "23", note: w.note,
            sort_order: i + 1,
          })),
      }),
    onSuccess: (nowa) => {
      kolejka.setQueryData(["budget", id], nowa);
      kolejka.invalidateQueries({ queryKey: ["budgets", code] });
      setBlad(null);
      setKomunikat("Zapisano.");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.isConflict) {
        setKonflikt(e.body.current as BudgetVersion);
        return;
      }
      setBlad(e instanceof ApiError ? e : null);
    },
  });

  const akcja = useMutation({
    mutationFn: ({ co, comment }: { co: string; comment?: string }) =>
      api.post<BudgetVersion>(`/budgets/${id}/${co}`,
                              comment === undefined ? undefined : { comment }),
    onSuccess: (nowa) => {
      kolejka.setQueryData(["budget", id], nowa);
      kolejka.invalidateQueries({ queryKey: ["budgets", code] });
      setBlad(null);
      setDecyzja(null);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const decyzjeCzesciowe = useMutation({
    mutationFn: (decisions: PartDecision[]) =>
      api.post<BudgetVersion>(`/budgets/${id}/decide-parts`, { decisions }),
    onSuccess: () => {
      kolejka.invalidateQueries({ queryKey: ["budget", id] });
      kolejka.invalidateQueries({ queryKey: ["report", code] });
      setCzesciowa(false);
      setKomunikat("Decyzje zapisane.");
      setBlad(null);
    },
    onError: (e) => setBlad(e instanceof ApiError ? e : null),
  });

  const suma = sumMoney(wiersze.map((w) => w.amount_net));
  const stanyPozycji = wersja?.lines ?? [];
  const odrzucone = stanyPozycji.filter((l) => l.approval_state === "rejected");
  const zatwierdzone = stanyPozycji.filter((l) => l.approval_state === "approved");
  const akcje = wersja?.available_actions ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <BackButton fallback={`/projekty/${code}`} />
        <Typography variant="h6">
          {code} · kosztorys v{wersja?.number}
        </Typography>
        {wersja && (
          <Chip size="small" label={STATUS_BUDZETU[wersja.status]?.label ?? wersja.status}
                color={STATUS_BUDZETU[wersja.status]?.color ?? "default"} />
        )}
        {wersja?.approval?.current_step && (
          <Typography variant="body2" color="text.secondary">
            czeka na: {wersja.approval.current_step.name}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>
          {formatMoney(suma)} zł
        </Typography>
        {akcje.includes("edit") && (
          <>
            <Button startIcon={<AddIcon />}
                    onClick={() => setWiersze((s) => [...s, { ...PUSTY }])}>
              Wiersz
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />}
                    onClick={() => zapisz.mutate()} disabled={zapisz.isPending}>
              Zapisz
            </Button>
          </>
        )}
        {akcje.includes("submit") && (
          <Button variant="outlined" onClick={() => akcja.mutate({ co: "submit" })}>
            Złóż do akceptacji
          </Button>
        )}
        {akcje.includes("decide_parts") && (
          <Button onClick={() => setCzesciowa(true)}>Decyduj o pozycjach</Button>
        )}
        {akcje.includes("approve") && (
          <>
            <Button color="error" onClick={() => setDecyzja("reject")}>Odrzuć</Button>
            <Button variant="contained" onClick={() => setDecyzja("approve")}>
              Zatwierdź całość
            </Button>
          </>
        )}
      </Stack>

      {blad && (
        <Alert severity="error" onClose={() => setBlad(null)}>
          {blad.message}
          {blad.body.problems.length > 0 && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
              {blad.body.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </Alert>
      )}

      {tylkoOdczyt && wersja && (
        <Alert severity="info">
          {/* Bez odmiany przez rodzaje: „Wersja odrzucony" to usterka,
              a utrzymywanie form gramatycznych dla każdego statusu
              to koszt bez wartości. */}
          Ta wersja jest tylko do odczytu (stan: {STATUS_BUDZETU[wersja.status]?.label}).
          Zmiany wprowadza się w nowej wersji.
        </Alert>
      )}

      {odrzucone.length > 0 && (
        <Alert severity="warning">
          {odrzucone.length === 1
            ? "Jedna pozycja wróciła do poprawy"
            : `${odrzucone.length} pozycji wróciło do poprawy`}{" "}
          ({formatMoney(sumMoney(odrzucone.map((l) => l.amount_net)))} zł).
          Zatwierdzone {zatwierdzone.length} poz. jest już wiążące — można na nich
          kontraktować, nie czekając na resztę.
        </Alert>
      )}

      {wersja?.approval?.decisions?.length ? (
        <DecisionHistory decyzje={wersja.approval.decisions} />
      ) : null}

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36 }} />
              {kolumny.map((c) => (
                <TableCell key={c.key} align={c.align ?? "left"}>{c.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <Sheet
            columns={kolumny} rows={wiersze} focus={focus} readOnly={tylkoOdczyt}
            onFocus={setFocus} onChange={zmien} onPasteBlock={wklej}
            // Odrzucone pozycje widać w arkuszu, a nie tylko w historii decyzji:
            // kierownik ma poprawiać konkretne wiersze, więc musi je znaleźć
            // wzrokiem, a nie zestawiając dwie listy.
            rowSx={(w) =>
              w.approval_state === "rejected"
                // Delikatny odcień, nie płachta: wiersz ma się rzucić w oczy
                // przy przeglądaniu, a nie utrudniać czytania kwot, które
                // kierownik właśnie poprawia.
                ? { "& td": { bgcolor: "rgba(211, 47, 47, 0.10)" } }
                : {}}
          />
        </Table>
      </Paper>

      {!tylkoOdczyt && (
        <Typography variant="caption" color="text.secondary">
          Kolumny można wkleić prosto z arkusza — zaznacz komórkę, od której
          zacząć, i wklej. Przecinek dziesiętny i spacje w tysiącach są rozumiane.
        </Typography>
      )}

      <PartialApproval
        open={czesciowa}
        lines={wersja?.lines ?? []}
        accountCodes={plan?.results ?? []}
        pending={decyzjeCzesciowe.isPending}
        onClose={() => setCzesciowa(false)}
        onSubmit={(decisions) => decyzjeCzesciowe.mutate(decisions)}
      />

      <DecisionDialog
        open={decyzja !== null}
        tryb={decyzja ?? "approve"}
        tytul={decyzja === "reject" ? "Odrzuć kosztorys" : "Zatwierdź kosztorys"}
        opis={decyzja === "reject"
          ? "Kosztorys wróci do kierownika produkcji z Twoim uzasadnieniem."
          : `Zatwierdzasz wersję v${wersja?.number} na ${formatMoney(suma)} zł. ` +
            "Pozycje zostaną zamrożone jako migawka."}
        blad={blad?.field("comment")}
        pracuje={akcja.isPending}
        onClose={() => { setDecyzja(null); setBlad(null); }}
        onConfirm={(comment) => akcja.mutate({ co: decyzja!, comment })}
      />

      <Dialog open={Boolean(konflikt)} onClose={() => setKonflikt(null)}>
        <DialogTitle>Ktoś zapisał wcześniej</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ten kosztorys zmienił się, odkąd go otworzyłeś — zapisana wersja ma
            numer {konflikt?.revision}, Twoja {wersja?.revision}. Wczytanie
            porzuci Twoje niezapisane zmiany.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKonflikt(null)}>Zostaw moje zmiany</Button>
          <Button variant="contained" onClick={() => {
            if (konflikt) {
              kolejka.setQueryData(["budget", id], konflikt);
              setWiersze(konflikt.lines.map((l) => ({ ...PUSTY, ...l })));
            }
            setKonflikt(null);
          }}>
            Wczytaj aktualną
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(komunikat)} autoHideDuration={2500}
                onClose={() => setKomunikat(null)} message={komunikat} />
    </Stack>
  );
}

